import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { Profile, GeneratedImage, Theme, GalleryImage } from '@/types';

// Profile operations
export const profileOperations = {
  async getProfile(userId: string): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        logger.error('Error fetching profile', error, { operation: 'getProfile', userId });
        return null;
      }
      
      logger.debug('Profile fetched successfully', { operation: 'getProfile', userId });
      return data;
    } catch (error) {
      logger.error('Exception in getProfile', error, { operation: 'getProfile', userId });
      return null;
    }
  },

  async updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) {
        logger.error('Error updating profile', error, { operation: 'updateProfile', userId, updates });
        return null;
      }
      
      logger.debug('Profile updated successfully', { operation: 'updateProfile', userId });
      return data;
    } catch (error) {
      logger.error('Exception in updateProfile', error, { operation: 'updateProfile', userId });
      return null;
    }
  }
};

// Payment operations (Edge Functions)
export const paymentOperations = {
  async verifyPayment(sessionId: string): Promise<{ success: boolean; error?: string } | null> {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { session_id: sessionId }
      });

      if (error) {
        logger.error('Error verifying payment', error, { operation: 'verifyPayment', sessionId });
        return null;
      }

      return data as { success: boolean; error?: string };
    } catch (error) {
      logger.error('Exception in verifyPayment', error, { operation: 'verifyPayment', sessionId });
      return null;
    }
  },

  async createPayment(credits: number, isQuickLoad: boolean = false): Promise<{ url?: string; error?: string } | null> {
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          credits,
          isQuickLoad
        }
      });

      if (error) {
        logger.error('Error creating payment', error, { operation: 'createPayment', credits, isQuickLoad });
        return null;
      }

      return data as { url?: string; error?: string };
    } catch (error) {
      logger.error('Exception in createPayment', error, { operation: 'createPayment', credits });
      return null;
    }
  }
};

// Account operations (Edge Functions)
export const accountOperations = {
  async resetAccount(userId: string): Promise<{ success: boolean; error?: string } | null> {
    try {
      const { data, error } = await supabase.functions.invoke('reset-user-account', {
        body: { userId }
      });

      if (error) {
        logger.error('Error resetting account', error, { operation: 'resetAccount', userId });
        return null;
      }

      return data as { success: boolean; error?: string };
    } catch (error) {
      logger.error('Exception in resetAccount', error, { operation: 'resetAccount', userId });
      return null;
    }
  }
};

// Generated images operations
export const imageOperations = {
  async getUserImages(userId: string): Promise<GeneratedImage[]> {
    const { data, error } = await supabase
      .from('generated_images')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      logger.error('Error fetching user images', error, { operation: 'getUserImages', userId });
      return [];
    }
    
    return data || [];
  },

  async getUserImageCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    
    if (error) {
      logger.error('Error fetching user image count', error, { operation: 'getUserImageCount', userId });
      return 0;
    }
    
    return count || 0;
  },

  async getUserECardCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .like('prompt', 'Create a black-and-white coloring page e-card design for%');
    
    if (error) {
      logger.error('Error fetching user e-card count', error, { operation: 'getUserECardCount', userId });
      return 0;
    }
    
    return count || 0;
  },

  async getUserColoringPageCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('generated_images')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .not('prompt', 'like', 'Create a black-and-white coloring page e-card design for%');
    
    if (error) {
      logger.error('Error fetching user coloring page count', error, { operation: 'getUserColoringPageCount', userId });
      return 0;
    }
    
    return count || 0;
  },

  async createImage(imageData: Omit<GeneratedImage, 'id' | 'created_at'>): Promise<GeneratedImage | null> {
    const { data, error } = await supabase
      .from('generated_images')
      .insert([imageData])
      .select()
      .single();
    
    if (error) {
      logger.error('Error creating image', error, { operation: 'createImage' });
      return null;
    }
    
    return data;
  },

  async deleteImage(imageId: string): Promise<boolean> {
    const { error } = await supabase
      .from('generated_images')
      .delete()
      .eq('id', imageId);
    
    if (error) {
      logger.error('Error deleting image', error, { operation: 'deleteImage', imageId });
      return false;
    }
    
    return true;
  }
};

// Theme operations
export const themeOperations = {
  async getAllThemes(): Promise<Theme[]> {
    const { data, error } = await supabase
      .from('themes')
      .select('*')
      .order('theme_name', { ascending: true });
    
    if (error) {
      logger.error('Error fetching themes', error, { operation: 'getAllThemes' });
      return [];
    }
    
    return data || [];
  },

  async getThemeById(themeId: string): Promise<Theme | null> {
    const { data, error } = await supabase
      .from('themes')
      .select('*')
      .eq('id', themeId)
      .single();
    
    if (error) {
      logger.error('Error fetching theme', error, { operation: 'getThemeById', themeId });
      return null;
    }
    
    return data;
  }
};

// Gallery images operations
export const galleryOperations = {
  async getAllGalleryImages(): Promise<GalleryImage[]> {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
    
    if (error) {
      logger.error('Error fetching gallery images', error, { operation: 'getAllGalleryImages' });
      return [];
    }
    
    return data || [];
  },

  async getGalleryImagesByTheme(theme: string): Promise<GalleryImage[]> {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('is_active', true)
      .eq('theme', theme)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
    
    if (error) {
      logger.error('Error fetching gallery images by theme', error, { operation: 'getGalleryImagesByTheme', theme });
      return [];
    }
    
    return data || [];
  },

  async incrementLikes(imageId: string): Promise<boolean> {
    // First get current value, then increment (keep original logic for now)
    const { data: currentData, error: fetchError } = await supabase
      .from('gallery_images')
      .select('likes')
      .eq('id', imageId)
      .single();
    
    if (fetchError || !currentData) {
      logger.error('Error fetching current likes', fetchError, { operation: 'incrementLikes', imageId });
      return false;
    }
    
    const { error } = await supabase
      .from('gallery_images')
      .update({ likes: currentData.likes + 1 })
      .eq('id', imageId);
    
    if (error) {
      logger.error('Error incrementing likes', error, { operation: 'incrementLikes', imageId });
      return false;
    }
    
    return true;
  },

  async incrementDownloads(imageId: string): Promise<boolean> {
    // First get current value, then increment (keep original logic for now)
    const { data: currentData, error: fetchError } = await supabase
      .from('gallery_images')
      .select('downloads')
      .eq('id', imageId)
      .single();
    
    if (fetchError || !currentData) {
      logger.error('Error fetching current downloads', fetchError, { operation: 'incrementDownloads', imageId });
      return false;
    }
    
    const { error } = await supabase
      .from('gallery_images')
      .update({ downloads: currentData.downloads + 1 })
      .eq('id', imageId);
    
    if (error) {
      logger.error('Error incrementing downloads', error, { operation: 'incrementDownloads', imageId });
      return false;
    }
    
    return true;
  }
};